import { Client, Predict, Callbacks, type Room } from "@colyseus/sdk";
import RAPIER from "@dimforge/rapier3d-compat";
import * as shared from "@fragout/shared";
import {
  ArenaState, MoveInput, Player, PREDICTED_FIELDS, stepPlayer, createEnv, createPlayerBody,
  ARENA_M0_MAP, INTERP_DELAY_MS, guestName, KINDS, KIND_NAMES, Kind, Phase, GrenadeState,
  TICK_RATE, SCORE_LIMIT, FRAG_COOLDOWN, KILL_FALL, THROW_SPEED, INTERP_DELAY_MS as INTERP_MS,
  stepBallistic, arcToFirstBounce, handPos, throwDir, replayTrace,
  type MapDef, type PhysicsEnv, type MoveState, type StepEvents, type KillMsg,
} from "@fragout/shared";
import { InputState } from "./input.js";
import { Renderer } from "./render.js";
import { Hud, type HudView, type HudPlayerView, type HudPadView } from "./hud.js";
import { Sfx } from "./audio.js";

const $ = (id: string) => document.getElementById(id) as HTMLElement;
const canvas = $("game") as HTMLCanvasElement;
const landing = $("landing");
const nameInput = $("name") as HTMLInputElement;
const botsSelect = $("bots") as HTMLSelectElement;
const roomLine = $("roomline");

const endpoint = import.meta.env.DEV ? "http://localhost:2567" : location.origin;
const client = new Client(endpoint);
const MAP: MapDef = ((shared as unknown as Record<string, unknown>).ROOFTOPS as MapDef | undefined) ?? ARENA_M0_MAP;
const sfx = new Sfx();

const savedName = localStorage.getItem("fragout.name") ?? "";
nameInput.value = savedName || guestName();
let hashRoom = location.hash.slice(1);
roomLine.textContent = hashRoom ? `Joining room ${hashRoom}` : "";

$("play").addEventListener("click", () => { void start(false); });
$("practice").addEventListener("click", () => { void start(true); });
nameInput.addEventListener("keydown", (e) => { if (e.key === "Enter") void start(false); });
canvas.addEventListener("pointerdown", () => sfx.unlock());

let started = false;
async function start(practice: boolean, auto = false) {
  if (started) return;
  started = true;
  sfx.unlock();
  const name = nameInput.value.trim() || guestName();
  localStorage.setItem("fragout.name", name);
  roomLine.textContent = "Connecting";
  try {
    await RAPIER.init();
    let room: Room<ArenaState> | undefined;
    const token = sessionStorage.getItem("fragout.token");
    const tokenRoom = sessionStorage.getItem("fragout.room");
    if (auto && token && tokenRoom && tokenRoom === hashRoom) {
      try { room = await client.reconnect<ArenaState>(token); } catch { room = undefined; }
    }
    if (!room) {
      if (practice) room = await client.create<ArenaState>("arena", { name, bots: Number(botsSelect.value), practice: true });
      else if (hashRoom) {
        try { room = await client.joinById<ArenaState>(hashRoom, { name }); }
        catch (err) {
          if (auto) throw err;
          // The link points at a room that has closed. Forget it and find a live one.
          hashRoom = "";
          history.replaceState(null, "", location.pathname);
          room = await client.joinOrCreate<ArenaState>("arena", { name });
        }
      }
      else room = await client.joinOrCreate<ArenaState>("arena", { name });
    }
    location.hash = room.roomId;
    sessionStorage.setItem("fragout.token", room.reconnectionToken);
    sessionStorage.setItem("fragout.room", room.roomId);
    landing.style.display = "none";
    canvas.focus();
    runGame(room, name);
  } catch (err) {
    started = false;
    console.error(err);
    if (auto) {
      // The old room is gone. Forget it so the next PLAY starts fresh.
      sessionStorage.removeItem("fragout.token");
      sessionStorage.removeItem("fragout.room");
      history.replaceState(null, "", location.pathname);
      hashRoom = "";
      roomLine.textContent = "That room has closed";
    } else {
      roomLine.textContent = `Could not join: ${(err as Error).message}`;
    }
  }
}

// A refresh mid match keeps the room hash and the reconnection token, so rejoin without a click.
if (hashRoom && sessionStorage.getItem("fragout.room") === hashRoom && sessionStorage.getItem("fragout.token")) {
  roomLine.textContent = `Rejoining room ${hashRoom}`;
  void start(false, true);
}

interface Ghost { x: number; y: number; z: number; vx: number; vy: number; vz: number; kind: number; seq: number }

/** Steps of interpolation delay, the amount the view of other players trails the server. */
const INTERP_STEPS = Math.round((INTERP_MS / 1000) * TICK_RATE);

function runGame(room: Room<ArenaState>, myName: string) {
  const renderer = new Renderer(canvas, MAP);
  const input = new InputState(canvas);
  const env: PhysicsEnv = createEnv(RAPIER, MAP.boxes);
  const hud = new Hud($("hudroot"), (action) => {
    sfx.uiClick();
    // Readying up or voting a rematch is a click, so it is the moment the browser will grant the mouse.
    if (action === "ready" || action === "vote" || action === "rematch") void input.lock();
    if (action === "ready") room.send("ready");
    else if (action === "addBot") room.send("addBot");
    else if (action === "removeBot") room.send("removeBot");
    else if (action === "vote" || action === "rematch") room.send("vote");
    else if (action === "back") room.send("back");
  });

  const wire = room.input({ type: MoveInput, mode: "reliable" });
  const predict = Predict.get(room, { mode: "lerp", delay: INTERP_DELAY_MS, name: "arena" });
  predict.attachAll("players", { fields: ["x", "y", "z"], mode: "lerp" });
  const spawns = predict.spawns("grenades", {
    // Only real throws can satisfy a prediction. A grenade the server drops when you die is
    // yours too, and would otherwise be matched to a throw you had not made yet.
    owned: (g) => g.owner === room.sessionId && g.throwSeq > 0,
    // Match the exact throw, not merely the next one to arrive. Order alone is not enough: a
    // rejected or dropped throw would shift every later prediction onto the wrong grenade.
    correlate: (local: Ghost, server) => local.seq === server.throwSeq,
    // The same sweep the aiming arc uses, against the same map the server has, so a predicted
    // grenade cannot sail through a wall while it waits for the server to confirm it.
    step: (l: Ghost, dt: number) => { stepBallistic(env, l, dt, l.kind); },
    fields: ["x", "y", "z"],
  });

  const callbacks = Callbacks.get(room);
  let me: Player | undefined;
  let reconciler: ReturnType<typeof predict.reconciler<Player, MoveInput>> | undefined;
  const others = new Map<string, Player>();
  let killerName = "";
  let deathAt = 0;
  let deathPos = { x: 0, y: 0, z: 0, yaw: 0 };
  let wasAlive = true;
  let wasCooking = false;
  // Incremented for every input sent. The step that consumes an input runs inside wire.send(), so
  // a throw fired from it reads the same id the server will see.
  let inputSeq = 0;
  let lastPhase = -1;
  let lastCountdown = -1;

  const events: StepEvents = {
    pinPull: () => {
      const c = renderer.camera.position;
      sfx.pin(c.x, c.y, c.z);
      sfx.hissStart("me", c.x, c.y, c.z);
    },
    throw: (kind, x, y, z, dx, dy, dz, speed) => {
      sfx.hissStop("me");
      spawns.spawn({ x, y, z, vx: dx * speed, vy: dy * speed, vz: dz * speed, kind, seq: inputSeq });
    },
    knifeThrow: (x, y, z) => { sfx.knifeThrow(x, y, z); },
    handDetonate: (kind, x, y, z) => { sfx.hissStop("me"); sfx.explosion(x, y, z, kind); renderer.boom(x, y, z, kind); },
    stabStart: () => { const c = renderer.camera.position; sfx.stab(c.x, c.y, c.z); },
    kickStart: () => { const c = renderer.camera.position; sfx.kick(c.x, c.y, c.z); },
  };

  callbacks.onAdd("players", (p, sid) => {
    if (sid === room.sessionId) {
      me = p;
      const pb = createPlayerBody(env, p.x, p.y, p.z);
      input.yaw = p.yaw;
      const stepEnv = { env, pb, events, replay: false };
      reconciler = predict.reconciler<Player, MoveInput>(p, {
        input: wire,
        fields: PREDICTED_FIELDS as unknown as (keyof Player & string)[],
        step: (ctx, state, cmd) => {
          // The server does not step the dead, so neither do we: no phantom cooks from inputs that were in flight at the kill.
          if (me && !me.alive) return;
          stepEnv.replay = ctx.isReplay;
          stepPlayer(ctx.dt, state as unknown as MoveState, cmd, stepEnv);
        },
        smoothMs: 100,
        snap: 1.5,
      });
    } else {
      others.set(sid, p);
    }
  });
  callbacks.onRemove("players", (_p, sid) => { others.delete(sid); renderer.removePlayer(sid); });

  room.onMessage("kill", (m: KillMsg) => {
    const who = m.kind === KILL_FALL || !m.killerName ? "the roof" : m.killerName;
    hud.killFeed(who, m.victimName, m.kind, m.killer === room.sessionId || m.victim === room.sessionId);
    if (m.victim === room.sessionId) { killerName = who; sfx.death(); }
  });
  room.onMessage("boom", (b: { x: number; y: number; z: number; kind: number; owner?: string; hand?: boolean }) => {
    // Our own hand detonation was already drawn from the predicted step.
    if (b.hand && b.owner === room.sessionId) return;
    renderer.boom(b.x, b.y, b.z, b.kind); sfx.explosion(b.x, b.y, b.z, b.kind);
  });
  room.onMessage("spawn", (m: { yaw: number }) => { input.yaw = m.yaw; input.pitch = 0; input.slot = 1; });
  room.onMessage("snd", (s: { name: string; x: number; y: number; z: number; owner?: string; speed?: number }) => {
    if (s.owner === room.sessionId && s.name !== "pickup") return;
    switch (s.name) {
      case "pin": sfx.pin(s.x, s.y, s.z); break;
      case "bounce": sfx.bounce(s.x, s.y, s.z, s.speed ?? 5); break;
      case "stab": case "stabHit": sfx.stab(s.x, s.y, s.z); break;
      case "knifeThrow": sfx.knifeThrow(s.x, s.y, s.z); break;
      case "knifeThunk": case "stick": sfx.knifeThunk(s.x, s.y, s.z); break;
      case "kick": sfx.kick(s.x, s.y, s.z); break;
      case "pickup": sfx.pickup(); break;
      case "padSpawn": sfx.bounce(s.x, s.y, s.z, 4); break;
      default: break;
    }
  });
  room.onMessage("hit", () => { hud.hitMarker(); sfx.hitMarker(); });
  room.onMessage("hurt", () => { hud.hurt(); sfx.hurt(); });
  room.onMessage("phase", (m: { phase: number }) => {
    if (m.phase === Phase.Playing) sfx.matchStart();
    if (m.phase === Phase.Results) sfx.matchEnd();
  });

  room.onLeave((code) => {
    if (code === 4000 || code === 1000) return;
    roomLine.textContent = `Disconnected (${code}). Reload to rejoin.`;
    landing.style.display = "flex";
  });
  room.onError((code, message) => console.error("room error", code, message));

  const grenadeKeep = new Set<number | string>();
  const knifeKeep = new Set<string>();
  const dropKeep = new Set<string>();
  const view: HudView = {
    phase: 0, phaseLeftS: 0, matchS: 0, myName, alive: true, respawnLeftS: 0, killerName: "",
    shield: 100, flashing: false, cookProgress: -1, cookKind: 0, slot: 1,
    slots: [{ kind: 1, ammo: -1, label: "FRAG" }, { kind: 0, ammo: 0, label: "EMPTY" }, { kind: 0, ammo: 0, label: "EMPTY" }, { kind: 0, ammo: 1, label: "KNIFE" }],
    fragCooldown: 0, knifeOut: false, knifeReturnS: 0, players: [], pads: [], scoreLimit: SCORE_LIMIT,
    leader: "", leaderScore: 0, myScore: 0, winner: "", rtt: 0, fps: 0, showScoreboard: false, pointerLocked: false,
    humans: 0, bots: 0, countdownS: 0, hint: "", lockHint: "",
  };

  let lastT = performance.now();
  let lastFrameAt = performance.now();
  const frame = (t: number) => {
    const dt = Math.min(0.1, (t - lastT) / 1000);
    lastT = t;
    const st = room.state;
    const phase = st?.phase ?? 0;
    const live = phase === Phase.Playing || phase === Phase.Overtime;
    const alive = me?.alive ?? true;

    // ---- inputs, one per fixed step
    const n = predict.tick(t);
    if (reconciler) {
      for (let i = 0; i < n; i++) {
        const d = wire.data;
        d.moveX = alive ? input.moveX : 0;
        d.moveZ = alive ? input.moveZ : 0;
        d.crouch = alive ? input.crouch : false;
        d.jump = alive ? input.takeJump() : false;
        d.fire = alive && live ? input.take("Mouse0", input.fire) : false;
        d.kick = alive && live ? input.take("KeyF", input.kick) : false;
        d.stab = alive && live ? input.take("KeyG", input.stab) || input.take("Mouse2", false) : false;
        d.interact = alive ? input.take("KeyE", input.interact) : false;
        d.slot = input.slot;
        d.yaw = input.yaw;
        d.pitch = input.pitch;
        // What we are drawing other players at right now. The server rewinds a stab by this much.
        d.viewStep = Math.max(0, (st?.tick ?? 0) - INTERP_STEPS);
        d.seq = ++inputSeq;
        wire.send();
      }
      if (n > 0) {
        input.endStep();
        // The sim owns the slot (a pickup can swap it, cooking can refuse a change); the keys only ask.
        input.slot = (reconciler.state as unknown as MoveState).slot;
      }
    }

    // ---- camera
    if (me && reconciler) {
      const s = reconciler.state as unknown as MoveState;
      if (alive) {
        renderer.placeCamera(reconciler.value("x"), reconciler.value("y"), reconciler.value("z"), input.yaw, input.pitch, s.crouch);
        if (!wasAlive) { sfx.respawn(); wasAlive = true; }
        deathPos = { x: s.x, y: s.y, z: s.z, yaw: input.yaw };
      } else {
        if (wasAlive) { wasAlive = false; deathAt = t; sfx.hissStop("me"); }
        renderer.placeDeathCamera(deathPos.x, deathPos.y, deathPos.z, deathPos.yaw, (t - deathAt) / 1000);
      }
      const cooking = alive && s.cookLeft >= 0;
      // The server can clear a cook (death, match end); the hiss must not outlive it.
      if (wasCooking && !cooking) sfx.hissStop("me");
      wasCooking = cooking;
      const handKind = !alive ? 0 : s.slot === 1 ? Kind.Frag : s.slot === 2 ? s.kind2 : s.slot === 3 ? s.kind3 : 0;
      renderer.setHand(handKind, cooking, alive && s.slot === 4, s.knifeOut);

      // The aiming arc, from the hand to the first bounce, simulated exactly like the throw itself.
      const arcKind = cooking ? s.cookKind : handKind;
      const ready = alive && live && arcKind > 0 && (cooking || arcKind !== Kind.Frag || s.fragCooldown === 0);
      if (ready) {
        const hp = handPos(s, input.yaw, input.pitch, env);
        const td = throwDir(hp.d);
        renderer.setArc(arcToFirstBounce(env, hp.x, hp.y, hp.z, td.x * THROW_SPEED, td.y * THROW_SPEED, td.z * THROW_SPEED, arcKind));
      } else {
        renderer.setArc(null);
      }
      if (cooking) {
        const fuse = KINDS[s.cookKind]?.fuse || 1;
        const c = renderer.camera.position;
        sfx.hissUpdate("me", 1 - s.cookLeft / fuse, c.x, c.y, c.z);
      }
    }
    const cam = renderer.camera.position;
    sfx.setListener(cam.x, cam.y, cam.z, input.yaw);

    // ---- everyone else
    for (const [sid, p] of others) {
      if (!p.connected || !p.alive) { renderer.hidePlayer(sid); continue; }
      renderer.placePlayer(sid, p.colour, predict.value(p, "x"), predict.value(p, "y"), predict.value(p, "z"), p.yaw, p.crouch);
    }

    // ---- grenades: own throws from the spawn store, everything else too
    grenadeKeep.clear();
    for (const e of spawns.entries()) {
      const kind = e.server?.kind ?? e.local?.kind ?? 1;
      const hot = !!e.server && e.server.fuseLeft >= 0 && e.server.fuseLeft < TICK_RATE * 0.5 && e.server.state !== GrenadeState.Stuck;
      renderer.placeGrenade(e.id, kind, spawns.value(e, "x"), spawns.value(e, "y"), spawns.value(e, "z"), hot);
      grenadeKeep.add(e.id);
    }
    renderer.pruneGrenades(grenadeKeep);

    knifeKeep.clear();
    if (st?.knives) for (const [id, k] of st.knives) { renderer.placeKnife(id, k.x, k.y, k.z, k.vx, k.vy, k.vz, k.stuck); knifeKeep.add(id); }
    renderer.pruneKnives(knifeKeep);
    dropKeep.clear();
    if (st?.drops) for (const [id, d] of st.drops) { renderer.placeDrop(id, d.kind, d.x, d.y, d.z); dropKeep.add(id); }
    renderer.pruneDrops(dropKeep);
    if (st?.pads) for (const [id, pad] of st.pads) renderer.setPad(id, pad.active);

    renderer.tick(dt);
    renderer.render();

    // ---- HUD
    if (st) {
      const s = reconciler ? (reconciler.state as unknown as MoveState) : undefined;
      view.phase = phase;
      view.phaseLeftS = st.phaseLeft / TICK_RATE;
      view.matchS = st.matchStep / TICK_RATE;
      view.alive = alive;
      view.myName = me?.name ?? myName;
      view.respawnLeftS = (me?.respawnLeft ?? 0) / TICK_RATE;
      view.killerName = killerName;
      view.shield = me?.shield ?? 100;
      view.flashing = me?.flashing ?? false;
      view.cookProgress = s && s.cookLeft >= 0 ? 1 - s.cookLeft / (KINDS[s.cookKind]?.fuse || 1) : -1;
      view.cookKind = s?.cookKind ?? 0;
      view.slot = s?.slot ?? 1;
      view.slots[1].kind = s?.kind2 ?? 0; view.slots[1].ammo = s?.ammo2 ?? 0; view.slots[1].label = s?.kind2 ? KIND_NAMES[s.kind2] : "EMPTY";
      view.slots[2].kind = s?.kind3 ?? 0; view.slots[2].ammo = s?.ammo3 ?? 0; view.slots[2].label = s?.kind3 ? KIND_NAMES[s.kind3] : "EMPTY";
      view.slots[3].ammo = s?.knifeOut ? 0 : 1;
      view.fragCooldown = s ? s.fragCooldown / FRAG_COOLDOWN : 0;
      view.knifeOut = s?.knifeOut ?? false;
      view.knifeReturnS = (s?.knifeReturn ?? 0) / TICK_RATE;
      const players: HudPlayerView[] = [];
      let leader = "", leaderScore = -1;
      // The map is not there on the very first frame, before any snapshot has decoded.
      for (const [sid, p] of st.players ?? []) {
        players.push({ id: sid, name: p.name, score: p.score, kills: p.kills, deaths: p.deaths, me: sid === room.sessionId, connected: p.connected, bot: p.bot, ready: p.vote, vote: p.vote });
        if (p.score > leaderScore) { leaderScore = p.score; leader = p.name; }
      }
      view.players = players;
      view.leader = leader; view.leaderScore = Math.max(0, leaderScore);
      view.myScore = me?.score ?? 0;
      const pads: HudPadView[] = [];
      for (const [id, pad] of st.pads ?? []) pads.push({ id, label: pad.label, kind: pad.kind, active: pad.active, nextInS: pad.nextIn / TICK_RATE, clock: pad.clock });
      view.pads = pads;
      view.winner = st.winner;
      view.rtt = room.clock.smoothedRtt();
      view.fps = renderer.engine.getFps();
      view.showScoreboard = input.scoreboard;
      view.pointerLocked = input.locked;
      view.lockHint = input.lockError;
      view.humans = st.humans;
      view.bots = players.filter((p) => p.bot).length;
      view.countdownS = phase === Phase.Countdown ? st.phaseLeft / TICK_RATE : 0;
      view.hint = `Share this link: ${location.href}`;
      hud.update(view);

      if (phase !== lastPhase) { lastPhase = phase; lastCountdown = -1; }
      if (phase === Phase.Countdown) {
        const c = Math.ceil(st.phaseLeft / TICK_RATE);
        if (c !== lastCountdown && c <= 3) { sfx.countdown(c); lastCountdown = c; }
      }
    }

    lastFrameAt = performance.now();
    schedule();
  };
  // One animation frame in flight at a time, whoever asked for it.
  let rafPending = false;
  const schedule = () => {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame((t) => { rafPending = false; frame(t); });
  };
  schedule();

  // A hidden or throttled tab stops requestAnimationFrame. Keep the fixed step alive from a timer so the
  // server keeps getting one input per step and the player does not freeze in place for everyone else.
  setInterval(() => { if (performance.now() - lastFrameAt > 40) frame(performance.now()); }, 8);

  (window as unknown as { __fragout: unknown }).__fragout = {
    // Determinism check: compare with `npm run trace` under Node.
    trace: () => replayTrace(RAPIER, MAP.boxes),
    room, predict, input, wire, spawns, sfx, hud,
    get reconciler() { return reconciler; },
    get me() { return me; },
    env,
  };
}
